import React, { useEffect, useState } from 'react';
import { VELLUM as T, SANS, MONO } from '../folio/kit';

/* A notice, drawn on the sheet.

   It was a white rounded card with a drop shadow, a coloured left edge and
   an emoji - four things the folio does not have. The system has no radius,
   no shadow and no glyph standing in for an icon, so a notice is what every
   other block on the sheet is: a ruled box with a mono heading and a mark
   drawn in the accent.

   The tones follow the ones the form apparatus already set. Something that
   went right is ruled in ink and marked in accent, exactly as the sent-
   message block on /contact is; something that needs attention is ruled in
   accent throughout, exactly as ErrorNote is. The palette has no red and no
   amber, and inventing them for a toast would put two colours on the site
   that the design system never declared. */

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
}

interface ToastProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

const TONES: Record<Toast['type'], { rule: string; mark: string; urgent: boolean }> = {
  success: { rule: T.ink, mark: T.accent, urgent: false },
  info: { rule: T.ink, mark: T.mute, urgent: false },
  error: { rule: T.accent, mark: T.accent, urgent: true },
  warning: { rule: T.accent, mark: T.accent, urgent: true },
};

/* Drawn, not typed. */
const Mark: React.FC<{ type: Toast['type']; color: string }> = ({ type, color }) => {
  if (type === 'success') {
    return (
      <svg aria-hidden width="13" height="13" viewBox="0 0 14 14" fill="none">
        <path d="M2 7.5 5.5 11 12 3.5" stroke={color} strokeWidth="1.8" strokeLinecap="square" />
      </svg>
    );
  }
  return (
    <span
      aria-hidden
      style={{ fontFamily: MONO, fontSize: 11, fontWeight: 500, lineHeight: 1, color }}
    >
      {type === 'info' ? 'i' : '!'}
    </span>
  );
};

export const ToastComponent: React.FC<ToastProps> = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const tone = TONES[toast.type];

  useEffect(() => {
    setIsVisible(true);
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onRemove(toast.id), 300);
    }, toast.duration || 5000);

    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onRemove]);

  return (
    <div
      /* An error interrupts; a confirmation does not. Both were silent to a
         screen reader before, which made every outcome on this site
         invisible unless it happened to be on screen. */
      role={tone.urgent ? 'alert' : 'status'}
      aria-live={tone.urgent ? 'assertive' : 'polite'}
      className="fo-toast"
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr) auto',
        gap: 12,
        alignItems: 'start',
        width: 340,
        maxWidth: '100%',
        padding: '13px 14px',
        background: T.ground,
        border: `1.5px solid ${tone.rule}`,
        borderRadius: 0,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateX(0)' : 'translateX(12px)',
      }}
    >
      <span style={{ paddingTop: 2, display: 'flex' }}>
        <Mark type={toast.type} color={tone.mark} />
      </span>

      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: MONO,
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: tone.urgent ? T.accent : T.ink,
          }}
        >
          {toast.title}
        </div>
        <div
          style={{
            fontFamily: SANS,
            fontSize: 13.5,
            lineHeight: 1.45,
            color: T.mute,
            marginTop: 5,
          }}
        >
          {toast.message}
        </div>
      </div>

      <button
        onClick={() => onRemove(toast.id)}
        aria-label="Dismiss"
        className="fo-toast-x"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 18,
          height: 18,
          padding: 0,
          background: 'transparent',
          border: 'none',
          borderRadius: 0,
          color: T.mute,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <svg aria-hidden width="9" height="9" viewBox="0 0 9 9" fill="none">
          <path d="M1 1l7 7M8 1 1 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square" />
        </svg>
      </button>
    </div>
  );
};

export default ToastComponent;
